import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'
import { getAccessToken, getActiveSession } from '../../lib/active-session'
import { type OfferSeller, sellerFromUser, tokenClaims } from '../../lib/offer-seller'

// Who may send an offer from the rate desk, and with which token. Publishing needs a signed-in seller
// (the Worker refuses anything else), so the panel asks here:
//   - on freightratesmcp.com: this site's own session (lib/active-session);
//   - inside the Navo24 workspace: the token reader the host hands its remotes (`getAccessToken`, the
//     same prop the host gives its other session-aware remotes), passed down by WorkspaceRates. A host
//     that hands none leaves the panel signed out there, which then says so and offers the samples.

type TokenReader = () => Promise<string | null>

const HostToken = createContext<TokenReader | null>(null)

export function OfferSessionProvider({
  getAccessToken: hostToken,
  children,
}: {
  getAccessToken?: TokenReader
  children: ReactNode
}) {
  return <HostToken.Provider value={hostToken ?? null}>{children}</HostToken.Provider>
}

export type OfferSessionState =
  | { status: 'checking' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; seller: OfferSeller }

export function useOfferSession(): OfferSessionState & { token: TokenReader } {
  const host = useContext(HostToken)
  const [state, setState] = useState<OfferSessionState>({ status: 'checking' })

  // A fresh token at the moment of publishing: the session client refreshes it when it is due.
  const token = useCallback<TokenReader>(
    () => (host ? host() : getAccessToken()).catch(() => null),
    [host]
  )

  useEffect(() => {
    let live = true
    const read = async (): Promise<OfferSeller | null> => {
      if (host) {
        const t = await host().catch(() => null)
        const claims = t ? tokenClaims(t) : null
        return claims ? sellerFromUser(claims) : null
      }
      const active = await getActiveSession()
      return active ? sellerFromUser(active.session.user) : null
    }
    read()
      .then((seller) => {
        if (live) setState(seller ? { status: 'signed-in', seller } : { status: 'signed-out' })
      })
      .catch(() => live && setState({ status: 'signed-out' }))
    return () => {
      live = false
    }
  }, [host])

  return { ...state, token }
}
